import { TimePicker } from '@frontend-platform/ui-lib';
import {
  Slider,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@frontend-platform/ui-lib/ui';
import { Info } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getLiveConversationCount } from '../../../api/api';
import { useCalibrationConfig } from '../../../hooks/use-calibration-config';
import type { CalibrationConfig } from '../../../lib/calibration-config';
import { LIVE_SAMPLE_RATE } from '../constants';
import { type CreateRunFormData, LIVE_START_MODE, type LiveStartMode } from '../types';
import { type BannerStatus, ConversationCountBanner } from './conversation-count-banner';
import {
  buildListeningWindowSummary,
  formatHHmm,
  getLiveCountWindowRange,
  getMsUntilNextMinute,
  getNextHourTimeStr,
  timeStrToTodayDate,
  toUTCISO,
} from './utils';

interface LiveScopeProps {
  formData: CreateRunFormData;
  setField: <K extends keyof CreateRunFormData>(key: K, value: CreateRunFormData[K]) => void;
}

// A failed getLiveConversationCount call is a distinct state from "not fetched
// yet" (count === null, banner hidden) and from "fetched a genuine zero"
// (statusFromCount's own failed branch) — it needs its own flag rather than
// being packed into `selectedConversationCount`, which is typed `number |
// null` and has no representation for "errored".
const LIVE_COUNT_FETCH_FAILED_STATUS: BannerStatus = {
  type: 'failed',
  message:
    'Unable to estimate conversation volume right now. This run may fail to meet the minimum conversation requirements for analysis.',
};

function statusFromCount(count: number | null, config: CalibrationConfig): BannerStatus | null {
  if (count === null) return null;
  if (!count) {
    return {
      type: 'failed',
      message:
        "Estimated conversations unavailable. We're unable to estimate conversation volume due to insufficient historical data.",
    };
  }
  const lower = config.conversationLowerLimit;
  const upper = config.conversationUpperLimit;
  if (count < lower) {
    return {
      type: 'warn',
      message: `~${count.toLocaleString()} conversations estimated. This run is unlikely to meet the minimum requirement of ${lower.toLocaleString()} conversations. Consider increasing the sample rate or extending the listening window.`,
    };
  }
  if (count > upper) {
    return {
      type: 'warn',
      message: `~${count.toLocaleString()} conversations estimated. This run is expected to exceed the analysis limit. Only ${upper.toLocaleString()} conversations will be analysed.`,
    };
  }
  return {
    type: 'info',
    message: `~${count.toLocaleString()} conversations estimated. This run is within the optimal range for analysis.`,
  };
}

// Pure mirror of the effectiveStart/effectiveEnd useMemos below, parameterized
// so triggerFetch can compute the ISO range for a value that was *just*
// passed to setField but hasn't landed in `formData` yet (setField is
// asynchronous — the state update it schedules isn't visible until the next
// render). Fetching straight from `formData`/the memoized ISOs here would
// always be one edit behind the value the user just changed.
function computeLiveRange(
  liveStartMode: LiveStartMode,
  startTime: string | null,
  endTime: string | null,
  now: Date
): { effectiveStart: Date | null; effectiveEnd: Date | null } {
  let effectiveStart: Date | null = null;
  if (liveStartMode === LIVE_START_MODE.NOW) {
    effectiveStart = new Date(now.getTime());
  } else if (startTime) {
    effectiveStart = timeStrToTodayDate(startTime);
    if (effectiveStart.getTime() < now.getTime())
      effectiveStart.setDate(effectiveStart.getDate() + 1);
  }

  let effectiveEnd: Date | null = null;
  if (endTime) {
    effectiveEnd = timeStrToTodayDate(endTime);
    if (effectiveStart && effectiveEnd.getTime() < effectiveStart.getTime()) {
      effectiveEnd.setDate(effectiveEnd.getDate() + 1);
    }
  }

  return { effectiveStart, effectiveEnd };
}

export function LiveScope({ formData, setField }: LiveScopeProps) {
  const { calibrationConfig } = useCalibrationConfig();
  const [now, setNow] = useState<Date>(() => new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetchError, setHasFetchError] = useState(false);
  const fetchSeq = useRef(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Align "now" to the next minute boundary, then tick every 60s.
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    const timeout = setTimeout(() => {
      setNow(new Date());
      interval = setInterval(() => setNow(new Date()), 60_000);
    }, getMsUntilNextMinute());
    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, []);

  // effectiveStart: NOW → current time; LATER → today at the chosen wall-clock
  // (rolled to tomorrow if the time already passed).
  const effectiveStart = useMemo<Date | null>(() => {
    if (formData.liveStartMode === LIVE_START_MODE.NOW) return new Date(now.getTime());
    if (!formData.startTime) return null;
    const start = timeStrToTodayDate(formData.startTime);
    if (start.getTime() < now.getTime()) start.setDate(start.getDate() + 1);
    return start;
  }, [formData.liveStartMode, formData.startTime, now]);

  const effectiveEnd = useMemo<Date | null>(() => {
    if (!formData.endTime) return null;
    const end = timeStrToTodayDate(formData.endTime);
    if (effectiveStart && end.getTime() < effectiveStart.getTime()) {
      end.setDate(end.getDate() + 1);
    }
    return end;
  }, [formData.endTime, effectiveStart]);

  const startISO = effectiveStart ? toUTCISO(effectiveStart) : null;
  const endISO = effectiveEnd ? toUTCISO(effectiveEnd) : null;

  // Sync the computed ISOs back into formData so the submit payload has them.
  useEffect(() => {
    setField('liveStartISO', startISO);
    setField('liveEndISO', endISO);
  }, [startISO, endISO, setField]);

  const summary = buildListeningWindowSummary(
    effectiveStart,
    effectiveEnd,
    now,
    formData.liveStartMode === LIVE_START_MODE.NOW
  );

  // Mirrors historical-scope.tsx's override-param pattern: each handler below
  // passes the value it just set, so the fetch uses that value directly
  // instead of the pre-update closure — see computeLiveRange's docblock.
  const triggerFetch = (
    next?: Partial<{
      liveStartMode: LiveStartMode;
      startTime: string | null;
      endTime: string | null;
      sampleRate: number;
    }>
  ) => {
    const liveStartMode = next?.liveStartMode ?? formData.liveStartMode;
    const startTime = next?.startTime !== undefined ? next.startTime : formData.startTime;
    const endTime = next?.endTime !== undefined ? next.endTime : formData.endTime;
    const sampleRate = next?.sampleRate ?? formData.sampleRate;
    const { effectiveStart: nextEffectiveStart, effectiveEnd: nextEffectiveEnd } = computeLiveRange(
      liveStartMode,
      startTime,
      endTime,
      now
    );
    // Cancel any pending fetch for the previous range before the guard below
    // can return early — otherwise clearing a field (e.g. End time) within the
    // debounce window bails here without cancelling, and the stale fetch still
    // lands and overwrites selectedConversationCount for a range that's no
    // longer selected.
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (!nextEffectiveStart || !nextEffectiveEnd) return;

    debounceTimer.current = setTimeout(() => {
      const seq = ++fetchSeq.current;
      setIsLoading(true);
      setField('isLoadingConversationCount', true);
      // The real window (nextEffectiveStart/End) is in the future — there's no
      // traffic to count yet, so the estimate call asks about the same
      // wall-clock hours 7 days back instead (getLiveCountWindowRange), not
      // the future window itself.
      const { startTime: windowStart, endTime: windowEnd } = getLiveCountWindowRange(
        nextEffectiveStart,
        nextEffectiveEnd,
        now
      );
      getLiveConversationCount({
        startTime: windowStart,
        endTime: windowEnd,
        sampleRate,
        botRefId: formData.evaluationBotRefId ?? null,
      })
        .then((count) => {
          if (seq !== fetchSeq.current) return;
          setHasFetchError(false);
          setField('selectedConversationCount', count);
        })
        .catch(() => {
          if (seq !== fetchSeq.current) return;
          setHasFetchError(true);
          setField('selectedConversationCount', null);
        })
        .finally(() => {
          if (seq !== fetchSeq.current) return;
          setIsLoading(false);
          setField('isLoadingConversationCount', false);
        });
    }, 800);
  };

  useEffect(
    () => () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    },
    []
  );

  // Default endTime to top-of-next-hour when in NOW mode, then fetch an
  // estimate for whatever values are in play at mount (the defaults just
  // seeded, or values already set from a prior visit to this step) — mirrors
  // aistudio's onMounted, which always ends with an unconditional
  // onTimePickerConfirm() so the banner shows an estimate immediately instead
  // of only after the user first touches a field.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only on first paint
  useEffect(() => {
    if (formData.liveStartMode === LIVE_START_MODE.NOW && !formData.endTime) {
      const nextEndTime = getNextHourTimeStr(new Date());
      setField('endTime', nextEndTime);
      triggerFetch({ endTime: nextEndTime });
    } else {
      triggerFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleModeChange = (mode: LiveStartMode) => {
    // A pending debounced fetch from before the mode switch must not still
    // land — it would overwrite selectedConversationCount (reset below) with
    // an estimate for a window that's no longer selected.
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setField('liveStartMode', mode);
    if (mode === LIVE_START_MODE.NOW) {
      setField('startTime', null);
      setField('endTime', getNextHourTimeStr(now));
    } else {
      setField('startTime', getNextHourTimeStr(now));
      setField('endTime', null);
    }
    setField('selectedConversationCount', null);
    setHasFetchError(false);
  };

  // Time inputs use 'HH:mm' from <input type="time" />; backend wants 'HH:mm:ss'.
  // Pass the new value straight to triggerFetch — formData won't reflect it
  // until the next render, so reading formData here would fetch the range
  // this change just replaced (see computeLiveRange's docblock).
  const onStartTimeChange = (value: string) => {
    const nextStartTime = value ? `${value}:00` : null;
    setField('startTime', nextStartTime);
    triggerFetch({ startTime: nextStartTime });
  };
  const onEndTimeChange = (value: string) => {
    const nextEndTime = value ? `${value}:00` : null;
    setField('endTime', nextEndTime);
    triggerFetch({ endTime: nextEndTime });
  };

  const onSampleRateChange = (rate: number) => {
    setField('sampleRate', rate);
    triggerFetch({ sampleRate: rate });
  };

  const status = hasFetchError
    ? LIVE_COUNT_FETCH_FAILED_STATUS
    : statusFromCount(formData.selectedConversationCount, calibrationConfig);
  const isStartNow = formData.liveStartMode === LIVE_START_MODE.NOW;

  return (
    <TooltipProvider>
      <section className="rounded-lg border border-border bg-background p-4 shadow-sm">
        <div className="mb-4 flex flex-col gap-1">
          <div className="flex items-center gap-1.5 font-medium text-foreground text-sm">
            <span>Live Production Data</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">
                Actual conversation volume may vary from estimates. Runs require a minimum of{' '}
                {calibrationConfig.conversationLowerLimit.toLocaleString()} conversations and are
                capped at {calibrationConfig.conversationUpperLimit.toLocaleString()} conversations.
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="text-muted-foreground text-xs">
            Configure how live conversations are collected for this run.
          </div>
        </div>

        <div className="my-4 h-px bg-border" />

        {/* Listening window header + mode toggle */}
        <div className="mb-3 flex items-start justify-between">
          <div className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-foreground">Listening window</span>
            <span className="text-muted-foreground text-xs">
              Set the start and end time for collecting live conversations.
            </span>
          </div>
          <div className="inline-flex rounded-md border border-border bg-background">
            {[LIVE_START_MODE.NOW, LIVE_START_MODE.LATER].map((mode) => {
              const selected = formData.liveStartMode === mode;
              return (
                <button
                  type="button"
                  key={mode}
                  onClick={() => handleModeChange(mode)}
                  className={[
                    'px-3 py-1.5 text-sm transition-colors first:rounded-l-md last:rounded-r-md',
                    selected
                      ? 'bg-neutral-900 text-white'
                      : 'bg-background text-foreground hover:bg-muted',
                  ].join(' ')}
                >
                  {mode === LIVE_START_MODE.NOW ? 'Start now' : 'Start later'}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time pickers */}
        <div className="mb-2 flex gap-4">
          <div className="flex flex-col gap-2">
            <span className="font-medium text-foreground text-sm">Start time</span>
            <TimePicker
              value={formData.startTime ? timeStrToTodayDate(formData.startTime) : undefined}
              onChange={(d) => onStartTimeChange(formatHHmm(d))}
              disabled={isStartNow}
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="font-medium text-foreground text-sm">End time</span>
            <TimePicker
              value={formData.endTime ? timeStrToTodayDate(formData.endTime) : undefined}
              onChange={(d) => onEndTimeChange(formatHHmm(d))}
            />
          </div>
        </div>

        {summary ? (
          <div className="my-3 rounded-md bg-muted px-3.5 py-2.5 text-muted-foreground text-sm">
            {summary.duration} | {summary.range}
          </div>
        ) : null}

        <div className="my-4 h-px bg-border" />

        <div className="flex flex-col gap-2 font-medium text-foreground text-sm">
          <span>Sample rate</span>
          <span className="font-normal text-muted-foreground text-xs">
            Choose the percentage of live conversations to include.
          </span>
        </div>

        <div className="mt-4 flex items-center gap-4">
          <Slider
            value={[formData.sampleRate]}
            min={LIVE_SAMPLE_RATE.MIN}
            max={LIVE_SAMPLE_RATE.MAX}
            step={1}
            onValueChange={(values: number[]) => {
              const v = values[0];
              if (typeof v === 'number') onSampleRateChange(v);
            }}
            className="flex-1"
          />
          {/* biome-ignore lint/nursery/useSortedClasses: manual ordering for readability */}
          <div className="w-12 text-right text-sm tabular-nums text-foreground">
            {formData.sampleRate}%
          </div>
        </div>
        <div className="mt-1 flex justify-between text-muted-foreground text-xs">
          <span>{LIVE_SAMPLE_RATE.MIN}%</span>
          <span>{LIVE_SAMPLE_RATE.MAX}%</span>
        </div>

        <div className="mt-3">
          <ConversationCountBanner loading={isLoading} status={status} />
        </div>
      </section>
    </TooltipProvider>
  );
}
